import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PageHeader1 from '../../components/common/PageHeader1';
import ChatTimerMaxPauseLimitConfig from './ChatTimerMaxPauseLimit';
import AcceptanceTimeoutsConfig from './AcceptanceTimeoutsConfig';

function PoojaSharingConfig() {
    const BASE_URL = process.env.REACT_APP_BASEURL;
    const getAuthToken = () => localStorage.getItem("astroVani-admin-token");

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            poojaSharingAstroPercentage: ''
        }
    });

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${BASE_URL}/admin/poojaSharingAstroPercentage`, {
                    headers: {
                        'Authorization': getAuthToken(),
                    },
                });

                if (!response.ok) throw new Error("Failed to fetch pooja sharing astro percentage");
                const data = await response.json();

                // Assuming the structure is similar to other config endpoints, e.g., data.data.value
                const configData = data?.data?.value ?? data?.data;

                if (configData !== undefined && configData !== null) {
                    reset({
                        poojaSharingAstroPercentage: configData
                    });
                }
            } catch (error) {
                console.error("Fetch error:", error);
                toast.error(error.message || "Failed to fetch details");
            }
        };

        fetchConfig();
    }, [BASE_URL, reset]);

    const onSubmit = async (data) => {
        try {
            const valStr = String(data.poojaSharingAstroPercentage);
            if (valStr.includes('.') || !/^\d+$/.test(valStr)) {
                toast.error("Only integer values are allowed (no decimals).");
                return;
            }

            const payload = {
                key: "poojaSharingAstroPercentage",
                value: parseInt(data.poojaSharingAstroPercentage, 10),
                description: "Commission percentage for astrologer recommending/sharing a Pooja booking"
            };

            const response = await fetch(`${BASE_URL}/admin/poojaSharingAstroPercentage`, {
                method: 'PATCH',
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (!response.ok || (result && result.success === false) || (result && result.status === 'error')) {
                throw new Error(result.message || "Failed to update config");
            }

            toast.success(result.message || "Config updated successfully");
        } catch (error) {
            console.error("Update error:", error);
            toast.error(error.message || "Failed to update config");
        }
    };

    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Pooja Share Config" />
            <div className="row g-3 mb-3">
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="row g-3 align-items-center">
                                    <div className="col-md-12">
                                        <label className="form-label">Astro Referral Share Percentage</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                step="1"
                                                onKeyDown={(e) => {
                                                    if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onPaste={(e) => {
                                                    const pasteData = e.clipboardData.getData('text');
                                                    if (pasteData.includes('.') || !/^\d+$/.test(pasteData.trim())) {
                                                        e.preventDefault();
                                                        toast.error("Only whole integer numbers are allowed");
                                                    }
                                                }}
                                                className={`form-control ${errors.poojaSharingAstroPercentage ? 'is-invalid' : ''}`}
                                                placeholder="Enter percentage"
                                                {...register('poojaSharingAstroPercentage', {
                                                    required: "Percentage is required",
                                                    min: { value: 0, message: "Minimum is 0" },
                                                    max: { value: 100, message: "Maximum is 100" },
                                                    validate: (val) => {
                                                        if (val === '' || val === null || val === undefined) return true;
                                                        if (String(val).includes('.') || !/^\d+$/.test(String(val))) {
                                                            return "Only integer values are allowed (no decimals)";
                                                        }
                                                        return true;
                                                    }
                                                })}
                                            />
                                            <span className="input-group-text">%</span>
                                            {errors.poojaSharingAstroPercentage && (
                                                <div className="invalid-feedback">
                                                    {errors.poojaSharingAstroPercentage.message}
                                                </div>
                                            )}
                                        </div>
                                        <small className="text-muted mt-2 d-block">
                                            Commission percentage for astrologer recommending/sharing a Pooja booking
                                        </small>
                                    </div>
                                    <div className="col-12 mt-4 text-end">
                                        <button type="submit" className="btn btn-primary">
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <ChatTimerMaxPauseLimitConfig />
                </div>
                <div className="col-md-12">
                    <AcceptanceTimeoutsConfig />
                </div>
            </div>
        </div>
    );
}

export default PoojaSharingConfig;
